/**
 * Notification data interface
 * Represents a notification as stored in the database
 */
export interface Notification {
  id: string;
  user_id: string;
  type: string;
  content: Record<string, any>;
  resource_type?: string;
  resource_id?: string;
  topic_id?: string;
  project_id?: string;
  is_read: boolean;
  created_at: string;
  read_at?: string;
}

/**
 * Formatted notification interface with additional UI-specific fields
 */
export interface FormattedNotification extends Notification {
  formattedMessage: string;
  formattedTime: string;
  statusClass: string;
}

/**
 * Input for marking a notification as read
 */
export interface MarkAsReadInput {
  notificationId: string;
}

/**
 * Notification operations result interface
 */
export interface NotificationOperationResult {
  success: boolean;
  notificationId?: string;
}

/**
 * Notification list result interface
 */
export interface NotificationListResult {
  notifications: Notification[];
  unreadCount: number;
}

/**
 * Filter options for notifications
 */
export interface NotificationFilters {
  types?: string[];
  onlyUnread?: boolean;
  projectId?: string;
}

/**
 * Format a notification for display
 * Adds UI-friendly fields like formatted messages and time
 * 
 * @param notification - The raw notification from the database
 * @returns A formatted notification with additional UI-friendly fields
 */
export function formatNotification(notification: Notification): FormattedNotification {
  // Format the message based on type
  let formattedMessage = 'New notification';
  
  switch (notification.type) {
    case 'task_assigned':
      formattedMessage = `You were assigned to task: ${notification.content.task_title || 'Untitled task'}`;
      break;
    case 'task_commented':
      formattedMessage = `New comment on your task: ${notification.content.task_title || 'Untitled task'}`;
      break;
    case 'project_invitation':
      formattedMessage = `You were invited to project: ${notification.content.project_name || 'Untitled project'}`;
      break;
    case 'task_deadline_approaching':
      formattedMessage = `Task deadline approaching: ${notification.content.task_title || 'Untitled task'}`;
      break;
    case 'mention':
      formattedMessage = `You were mentioned in ${notification.content.resource_type || 'a comment'}`;
      break;
    default:
      if (notification.content?.message) {
        formattedMessage = notification.content.message;
      }
  }
  
  // Format time (would use date-fns in real implementation)
  const createdDate = new Date(notification.created_at);
  const now = new Date();
  const diffMs = now.getTime() - createdDate.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  let formattedTime = '';
  if (diffMins < 60) {
    formattedTime = `${diffMins} min${diffMins === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    formattedTime = `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else {
    formattedTime = `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }
  
  // Status class for styling
  const statusClass = notification.is_read ? 'read' : 'unread';
  
  return {
    ...notification,
    formattedMessage,
    formattedTime,
    statusClass
  };
}

/**
 * Apply filters to a list of notifications
 * 
 * @param notifications - List of notifications to filter
 * @param filters - Filter criteria to apply
 * @returns Filtered list of notifications
 */
export function filterNotifications(
  notifications: Notification[], 
  filters?: NotificationFilters
): Notification[] {
  if (!filters) return notifications;
  
  return notifications.filter(notification => {
    // Filter by type
    if (filters.types && filters.types.length > 0) {
      if (!filters.types.includes(notification.type)) {
        return false;
      }
    }
    
    // Filter by read status
    if (filters.onlyUnread && notification.is_read) {
      return false;
    }
    
    // Filter by project
    if (filters.projectId && notification.project_id !== filters.projectId) {
      return false;
    }
    
    // Passed all filters
    return true;
  });
}